import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GetCommunityPostsDocument,
  GetCommunityPostsQuery,
  GetCommunityPostsQueryVariables,
  useGetCommunityPostsQuery,
} from 'generated/graphql';
import { useAsyncQueryRaw } from '@client/new/helpers/gql';
import { Async, toAsyncFetched } from '@client/new/helpers/types';
import { Post, PostId } from '../model/post.types';
import { toPost } from '../model/post.mappers';
import { CommunityId } from '../model/community.types';
import { FeedTab, toCommunityFeedFilters } from '../model/feedFilter.types';
import { resolveInfluencerId } from '../lib/resolveInfluencerId';

const FEED_POLL_INTERVAL_MS = 30_000;
const OLDER_PAGE_DIRECTION = 'olderThan';
// Mirrors community-service's FEED_PAGE_SIZE. A page that came back short is
// the last one, so there is nothing to ask for.
const FEED_PAGE_SIZE = 25;

export type CommunityFeedResult = {
  posts: Async<Post[]>;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
};

type OlderPage = { cursor: PostId; posts: Post[] };

const toPosts = (data: GetCommunityPostsQuery): Post[] =>
  data.communityPosts ? data.communityPosts.posts.map(toPost) : [];

const dedupeById = (posts: Post[]): Post[] => {
  const seen = new Set<PostId>();
  const result: Post[] = [];
  for (const post of posts) {
    if (seen.has(post.id)) continue;
    seen.add(post.id);
    result.push(post);
  }
  return result;
};

export const useCommunityFeed = (
  communityId: CommunityId,
  canModerate: boolean,
  tab: FeedTab = 'all',
  influencerId?: string,
  search = '',
): CommunityFeedResult => {
  const client = useApolloClient();
  const filters = useMemo(
    () =>
      toCommunityFeedFilters(tab, resolveInfluencerId(influencerId), search),
    [tab, influencerId, search],
  );
  const query = useGetCommunityPostsQuery({
    variables: { id: communityId, canModerate, ...(filters && { filters }) },
    fetchPolicy: 'cache-and-network',
    pollInterval: FEED_POLL_INTERVAL_MS,
  });

  const { data: firstPage } = useAsyncQueryRaw({ query, mapper: toPosts });

  // Older pages are normalized into the cache under their own
  // posts(direction, lastId, ...) field entries and mirrored into hook state
  // through cache-only watchers, so optimistic post updates (delete, pin,
  // reactions) reach them; the 30s first-page poll rewrites only the
  // argument-less posts entry and cannot clobber them.
  const [olderPages, setOlderPages] = useState<OlderPage[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const unsubscribersRef = useRef<Array<() => void>>([]);
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current += 1;
    setOlderPages([]);
    setIsLoadingMore(false);
    setReachedEnd(false);
    const unsubscribers = unsubscribersRef.current;
    return () => {
      unsubscribers.splice(0).forEach((unsubscribe) => unsubscribe());
    };
  }, [communityId, canModerate, filters]);

  const posts: Async<Post[]> =
    firstPage.type === 'fetched'
      ? toAsyncFetched(
          dedupeById([
            ...firstPage.value,
            ...olderPages.flatMap((page) => page.posts),
          ]),
        )
      : firstPage;

  const lastPageSize =
    olderPages.length > 0
      ? olderPages[olderPages.length - 1].posts.length
      : firstPage.type === 'fetched'
      ? firstPage.value.length
      : 0;

  const hasMore =
    posts.type === 'fetched' && lastPageSize >= FEED_PAGE_SIZE && !reachedEnd;

  const loadMore = useCallback(() => {
    if (isLoadingMore || reachedEnd) return;
    if (posts.type !== 'fetched' || posts.value.length === 0) return;
    const cursor = posts.value[posts.value.length - 1].id;
    const variables: GetCommunityPostsQueryVariables = {
      id: communityId,
      canModerate,
      direction: OLDER_PAGE_DIRECTION,
      lastId: cursor,
      ...(filters && { filters }),
    };
    const generation = generationRef.current;

    setIsLoadingMore(true);
    client
      .query<GetCommunityPostsQuery, GetCommunityPostsQueryVariables>({
        query: GetCommunityPostsDocument,
        variables,
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        if (generationRef.current !== generation) return;
        const page = toPosts(data);
        if (page.length === 0) {
          setReachedEnd(true);
          return;
        }
        setOlderPages((prev) => [...prev, { cursor, posts: page }]);
        const watcher = client.watchQuery<
          GetCommunityPostsQuery,
          GetCommunityPostsQueryVariables
        >({
          query: GetCommunityPostsDocument,
          variables,
          fetchPolicy: 'cache-only',
        });
        const subscription = watcher.subscribe((result) => {
          if (generationRef.current !== generation) return;
          if (!result.data?.communityPosts) return;
          const updated = toPosts(result.data);
          setOlderPages((prev) =>
            prev.map((page) =>
              page.cursor === cursor ? { cursor, posts: updated } : page,
            ),
          );
        });
        unsubscribersRef.current.push(() => subscription.unsubscribe());
      })
      .catch(() => setReachedEnd(true))
      .finally(() => setIsLoadingMore(false));
  }, [
    client,
    communityId,
    canModerate,
    filters,
    isLoadingMore,
    reachedEnd,
    posts,
  ]);

  return { posts, hasMore, isLoadingMore, loadMore };
};
